import {
  getFontCss,
  HoverableLinkSizes,
  Processing,
  styleConstants,
} from '@paywithextend/components'
import { Moment } from 'moment'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AccountVerificationResendCodeTooltip from 'src/components/AccountVerificationResendCodeTooltip/AccountVerificationResendCodeTooltip'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgStatusSummaryActionRequiredRed from 'src/components/Svgs/SvgStatusSummaryActionRequiredRed'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import VerificationInputField from 'src/components/VerificationInputField/VerificationInputField'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { accountOverviewUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type AccountVerificationFlatFields = {
  verificationCode: string
}

interface AccountVerificationRequiredBoxProps {
  accountResendVerificationError?: string
  accountVerificationError?: string
  canVerifyAgain: boolean
  handleResendVerification: () => void
  handleVerifyCreditCard: (code: string) => void
  postingAccountVerification?: boolean
  postingResendAccountVerification?: boolean
  timeUntilNextVerificationRequested?: Moment
  canUpdateCreditCard?: boolean
}

const AccountVerificationRequiredBox: React.FC<
  AccountVerificationRequiredBoxProps
> = ({
  accountVerificationError,
  accountResendVerificationError,
  canVerifyAgain,
  handleResendVerification,
  handleVerifyCreditCard,
  postingAccountVerification,
  postingResendAccountVerification,
  timeUntilNextVerificationRequested,
  canUpdateCreditCard,
}): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()

  const flatFieldsValidation =
    (): FormFieldsValidation<AccountVerificationFlatFields> => ({
      verificationCode: [stringIsEmpty()],
    })
  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<AccountVerificationFlatFields>(
    {
      verificationCode: '',
    },
    flatFieldsValidation,
  )

  const onHandleResendVerification = (): void => {
    if (canVerifyAgain) {
      analytics?.track('account detail - send new code - text link press', {
        intent: 'send new code',
        interaction: 'text link press',
        screen: accountOverviewUrl(':id'),
      })
      handleResendVerification()
    }
  }

  const disableButton = postingAccountVerification && canUpdateCreditCard

  const hours = timeUntilNextVerificationRequested
    ? timeUntilNextVerificationRequested.format('H')
    : ''
  const minutes = timeUntilNextVerificationRequested
    ? timeUntilNextVerificationRequested.format('m')
    : ''

  return (
    <Box
      css={css`
        align-items: center;
        align-self: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 500px;
      `}
      data-testid="verification-required-box"
    >
      <SvgStatusSummaryActionRequiredRed />
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          margin-top: 10px;
          ${getFontCss(
            16,
            styleConstants.colors.red_dark,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <h3>{translate('Verification required')}</h3>
      </div>
      <div
        css={css`
          margin-top: 20px;
        `}
      >
        {translate('One last step before you can use your card with Extend.')}
      </div>
      <div
        css={css`
          margin-top: 20px;
        `}
      >
        <ol
          css={css`
            margin-left: 25px;
          `}
        >
          <li>
            {translate(
              '${checkYourRecentAuthorizationActivity}. Look for a temporary charge with "EX" in the description.',
              {
                checkYourRecentAuthorizationActivity: (
                  <b>{translate('Check your recent authorization activity')}</b>
                ),
              },
            )}
          </li>
          <li
            css={css`
              margin-top: 20px;
            `}
          >
            {translate(
              '${noteTheFiveLetterCodeAfterEx} and enter it below. Need help? Visit our ${resourceCenter} for support and contact options.',
              {
                noteTheFiveLetterCodeAfterEx: (
                  <b>{translate('Note the 5-letter code after "EX"')}</b>
                ),
                resourceCenter: (
                  <ResourceCenterLink underline size={HoverableLinkSizes.Large}>
                    {translate('customer support')}
                  </ResourceCenterLink>
                ),
              },
            )}
          </li>
        </ol>
      </div>
      <VerificationInputField
        fieldLabel={'Verification Code'}
        onChange={(val: string): void => {
          onFlatFieldChange('verificationCode', val)
        }}
        placeholder={translateSingleString(locale, 'Enter the 5-letter code')}
        value={flatFields['verificationCode']}
        errors={flatFieldsErrors['verificationCode']}
        data-testid="code-input"
        css={css`
          margin-bottom: 20px;
          margin-top: 40px;
          width: 100%;
        `}
      />
      <Button
        big={true}
        design={ButtonDesigns.designCta}
        disabled={disableButton}
        loading={postingAccountVerification}
        onClick={(): void => {
          if (flatFieldsSetErrors() && canUpdateCreditCard) {
            analytics?.track('account detail - verify account - button press', {
              intent: 'verify account',
              interaction: 'button press',
              screen: accountOverviewUrl(':id'),
            })
            handleVerifyCreditCard(flatFields['verificationCode'])
          }
        }}
        data-testid={'verify-button'}
        text={'Verify Account'}
        type={ButtonTypes.submit}
        css={css`
          width: 100%;
        `}
      />
      {accountVerificationError !== undefined && (
        <TopLevelError
          error={accountVerificationError}
          css={css`
            margin-top: 20px;
          `}
        />
      )}
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-bottom: 20px;
          margin-top: 20px;
        `}
      >
        <HoverableLink
          disabled={!canVerifyAgain || postingResendAccountVerification}
          underline
          onClick={onHandleResendVerification}
          ariaLabelSource={'Send new code'}
        >
          {'Send new code'}
        </HoverableLink>
        <AccountVerificationResendCodeTooltip
          active={!canVerifyAgain}
          hours={hours}
          minutes={minutes}
        />
        {postingResendAccountVerification && <Processing />}
      </div>
      {accountResendVerificationError !== undefined && (
        <TopLevelError
          error={accountResendVerificationError}
          css={css`
            margin-top: 20px;
          `}
        />
      )}
    </Box>
  )
}

export default AccountVerificationRequiredBox
